/**
 * Expense Capture — API client.
 * Thin typed wrapper over fetchApi (which unwraps the {success,data} envelope).
 */

import { fetchApi } from './api';
import type {
    ExpenseCategory, ExpenseCompany, ExpenseReceipt, ExpenseReceiptFile,
    ExtractedReceipt, CreateReceiptResult,
} from '../types/expense';

function qs(params: Record<string, string | number | undefined | null>): string {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
    }
    const s = sp.toString();
    return s ? `?${s}` : '';
}

export interface ReceiptUpdateInput {
    companyCode?: string | null;
    categoryId?: number | null;
    expenseDate?: string | null;
    vendor?: string | null;
    grossAmount?: number | null;
    vatAmount?: number | null;
    netAmount?: number | null;
    currency?: string | null;
    description?: string | null;
    declaration?: Record<string, unknown> | null;
}

export interface ReceiptListOpts {
    status?: string;
    companyCode?: string;
    month?: string;
    q?: string;
    userEmail?: string;
    limit?: number;
}

export const expenseCaptureApi = {
    listCategories() {
        return fetchApi<{ categories: ExpenseCategory[] }>(`/expenses/categories`);
    },
    listCompanies() {
        return fetchApi<{ companies: ExpenseCompany[]; defaultCompanyCode: string | null }>(`/expenses/companies`);
    },

    createReceipt(body: { companyCode: string; fileBase64: string; mimeType: string; fileName?: string }) {
        return fetchApi<CreateReceiptResult>(`/expenses/receipts`, {
            method: 'POST', body: JSON.stringify(body),
        });
    },
    ocrReceipt(id: number) {
        return fetchApi<{ receiptId: number; extracted: ExtractedReceipt; categoryId: number | null }>(
            `/expenses/receipts/${id}/ocr`, { method: 'POST' });
    },
    updateReceipt(id: number, body: ReceiptUpdateInput) {
        return fetchApi<{ receiptId: number }>(`/expenses/receipts/${id}`, {
            method: 'PUT', body: JSON.stringify(body),
        });
    },
    submitReceipt(id: number) {
        return fetchApi<{ receiptId: number; status: string }>(
            `/expenses/receipts/${id}/submit`, { method: 'POST' });
    },
    deleteReceipt(id: number) {
        return fetchApi<{ receiptId: number; deleted: boolean }>(
            `/expenses/receipts/${id}`, { method: 'DELETE' });
    },

    listReceipts(opts: ReceiptListOpts = {}) {
        return fetchApi<{ receipts: ExpenseReceipt[] }>(
            `/expenses/receipts${qs({ status: opts.status, companyCode: opts.companyCode, month: opts.month, q: opts.q, limit: opts.limit })}`);
    },
    getReceipt(id: number) {
        return fetchApi<{ receipt: ExpenseReceipt; files: ExpenseReceiptFile[] }>(`/expenses/receipts/${id}`);
    },
    getFileUrl(id: number, page = 1) {
        return fetchApi<{ url: string; mimeType: string | null; expiresInSeconds: number }>(
            `/expenses/receipts/${id}/file-url${qs({ page })}`);
    },

    managerList(opts: ReceiptListOpts = {}) {
        return fetchApi<{ receipts: ExpenseReceipt[]; scope: string | string[] }>(
            `/expenses/manager/receipts${qs({ status: opts.status, companyCode: opts.companyCode, month: opts.month, q: opts.q, userEmail: opts.userEmail, limit: opts.limit })}`);
    },
};
